/**
 * MandantDocumentList — Sektion in MandantCaseHome, die alle 5 Doc-Type-Boxen
 * rendert (BWA, EKS, Mietvertrag, Steuernummer-Nachweis, Sonstiges).
 *
 * Wir laden die komplette Liste EINMAL und übergeben den jeweils gefilterten
 * Slice an jede Box (statt 5 parallele Roundtrips).
 *
 * Phase B — BWA-Konsolidierung:
 *   - Es gibt nur noch EINE BWA-Upload-Stelle (diese Box hier).
 *   - Die alte `<MandantBwaSection>` (separater bwa-uploads-Bucket) ist
 *     entfernt; stattdessen führt `MandantDocumentBox` für `docType='bwa'`
 *     einen zusätzlichen `linkBwaToFinancialDataset()`-Call durch, sodass
 *     der Score-Engine-Pfad (financial_dataset.raw_bwa_storage_path)
 *     synchron bleibt.
 *   - Direkt unter der BWA-Box rendern wir den optionalen „Ich habe keine
 *     BWA — Daten manuell eintragen"-Disclosure (siehe `ManualBwaDisclosure`).
 */

import { useCallback, useEffect, useState } from 'react';
import { Calculator } from 'lucide-react';
import { de } from '../../lib/i18n/de';
import {
  DOCUMENT_TYPES,
  listCaseDocuments,
  type CaseDocument,
  type DocumentType,
} from '../../lib/supabaseDocuments';
import { useAuthOrSession } from '../../lib/authOrSession';
import { MandantDocumentBox } from './MandantDocumentBox';
import { MoneyInput } from '../finance/MoneyInput';
import { HoursInput } from '../finance/HoursInput';
import { FieldHelp } from '../forms/FieldHelp';
import { useWizardStore } from '../../lib/store/wizard';
import type { FieldHelpKey } from '../../lib/i18n/fieldHelp';

interface MandantDocumentListProps {
  readonly caseId: string;
  /** Wenn true, alle Boxen read-only (Berater/Demo-Sicht). */
  readonly readOnly?: boolean;
}

const ORDER: ReadonlyArray<DocumentType> = [
  'bwa',
  'eks',
  'mietvertrag',
  'steuernummer_nachweis',
  'sonstiges',
];

export function MandantDocumentList({ caseId, readOnly = false }: MandantDocumentListProps) {
  const { isMock } = useAuthOrSession();
  const [docs, setDocs] = useState<ReadonlyArray<CaseDocument> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (isMock) {
      // Demo-Modus: leere Liste — Boxen rendern Empty-State.
      setDocs([]);
      return;
    }
    try {
      const list = await listCaseDocuments(caseId, { onlyActive: true });
      setDocs(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : de.mandant.documents.errorUploadFailed);
      setDocs([]); // damit Boxen rendern können
    }
  }, [caseId, isMock]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <section
      className="mt-10 border-t border-stone-200 pt-8"
      aria-labelledby="mandant-docs-heading"
    >
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {de.mandant.documents.sectionLabel}
      </p>
      <h2
        id="mandant-docs-heading"
        className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900"
      >
        {de.mandant.documents.sectionTitle}
      </h2>
      <p className="mt-2 max-w-prose font-serif text-sm italic text-stone-600">
        {de.mandant.documents.sectionIntro}
      </p>

      {error && (
        <p
          role="alert"
          className="mt-4 rounded-sm border border-red-200 bg-red-50 px-4 py-2 font-serif text-sm text-red-800"
        >
          {error}
        </p>
      )}

      <div className="mt-6 space-y-4">
        {ORDER.map((docType) => {
          const meta = DOCUMENT_TYPES[docType];
          if (!meta) return null;
          return (
            <div key={docType}>
              <MandantDocumentBox
                caseId={caseId}
                docType={docType}
                mode={docType === 'sonstiges' ? 'multi' : 'single'}
                initialDocuments={docs ?? undefined}
                onChange={() => {
                  void refresh();
                }}
                readOnly={readOnly}
              />
              {/* Phase B: Manuelle BWA-Eingabe direkt unter der BWA-Box als
                  Disclosure — ehemals separate MandantBwaSection. */}
              {docType === 'bwa' && !readOnly && <ManualBwaDisclosure caseId={caseId} />}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------------
// ManualBwaDisclosure — „Ich habe keine BWA"-Pfad
// -----------------------------------------------------------------------------

/**
 * Inline-Disclosure unter der BWA-Box: erlaubt dem Mandanten, die wichtigsten
 * BWA-Eckwerte (Umsatz, Kosten, Stunden, Liquidität) manuell einzutragen,
 * wenn er keine BWA-Datei hat. Die Werte landen im Wizard-Store und
 * fließen so in den Score / die Stellungnahme.
 *
 * Da `raw_bwa_storage_path` in dem Fall NULL bleibt, sieht der Berater am
 * Status-Board, dass keine BWA-Datei vorliegt — und kann beim nächsten
 * Termin nachfragen.
 */
function ManualBwaDisclosure({ caseId }: { readonly caseId: string }) {
  const resources = useWizardStore((s) => s.cases[caseId]?.resources);
  const setField = useWizardStore((s) => s.setResourcesField);
  const ensureCase = useWizardStore((s) => s.ensureCase);

  // Defensive: falls der WizardStore noch keinen Slice für diesen Case hat.
  if (!resources) {
    ensureCase(caseId);
  }
  const r = resources ?? null;

  return (
    <details className="mt-3 rounded-sm border border-stone-200 bg-white">
      <summary className="-mx-1 inline-flex w-full cursor-pointer items-center gap-2 rounded-sm px-4 py-3 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 hover:text-stone-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2">
        <Calculator size={14} aria-hidden="true" />
        {de.mandant.bwaUpload.manualToggleOpen}
      </summary>

      <div className="space-y-8 border-t border-stone-100 px-5 py-6 md:px-7 md:py-7">
        <p className="max-w-prose font-serif text-sm italic leading-relaxed text-stone-600 md:text-base">
          {de.mandant.bwaUpload.manualIntro}
        </p>

        <ManualField labelKey="resources.bwa.revenue">
          <MoneyInput
            valueCents={r?.revenueCents ?? null}
            onChange={(cents) => {
              setField(caseId, 'revenueCents', cents);
            }}
            label={de.steps.resources.subSteps.bwa.revenueLabel}
            hint={de.steps.resources.subSteps.bwa.revenueHint}
            placeholder="z.B. 4.500,00"
          />
        </ManualField>

        <ManualField labelKey="resources.bwa.costs">
          <MoneyInput
            valueCents={r?.costsCents ?? null}
            onChange={(cents) => {
              setField(caseId, 'costsCents', cents);
            }}
            label={de.steps.resources.subSteps.bwa.costsLabel}
            hint={de.steps.resources.subSteps.bwa.costsHint}
            placeholder="z.B. 1.800,00"
          />
        </ManualField>

        <ManualField labelKey="resources.rate.hours">
          <HoursInput
            value={r?.hoursPerMonth ?? null}
            onChange={(hours) => {
              setField(caseId, 'hoursPerMonth', hours);
            }}
            label={de.steps.resources.subSteps.rate.hoursLabel}
            hint={de.steps.resources.subSteps.rate.hoursHint}
            placeholder="z.B. 140"
          />
        </ManualField>

        <ManualField labelKey="resources.liquidity.assets">
          <MoneyInput
            valueCents={r?.liquidityCents ?? null}
            onChange={(cents) => {
              setField(caseId, 'liquidityCents', cents);
            }}
            label={de.steps.resources.subSteps.liquidity.assetsLabel}
            hint={de.steps.resources.subSteps.liquidity.assetsHint}
            placeholder="z.B. 3.500,00"
          />
        </ManualField>
      </div>
    </details>
  );
}

function ManualField({
  labelKey,
  children,
}: {
  readonly labelKey: FieldHelpKey;
  readonly children: React.ReactNode;
}) {
  return (
    <div>
      {children}
      <div className="mt-2">
        <FieldHelp pathKey={labelKey} />
      </div>
    </div>
  );
}
